/**
 * A guide's history — a tab beside the guide itself, so switching between what
 * it says now and how it got here doesn't lose your place in the list.
 *
 * Restoring writes a new version carrying the old text — the history stays true
 * — and it does NOT publish. A restored version is content like any other and
 * goes live when someone approves it.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Banner, Button, Skeleton, Table } from '@servicecompressionllc/sc-design-system';
import { useMe } from '../../hooks/useMe.js';
import { canWriteKnowledge } from '../../lib/knowledgeAccess.js';
import { MarkdownViewer } from '../../components/MarkdownViewer.js';
import { knowledgeApi, type KnowledgeItemRevision } from './api.js';

/** Plain words: a reviewer shouldn't have to know the enum names. */
const SOURCE_LABEL: Record<KnowledgeItemRevision['editSource'], string> = {
  GENERATION: 'written by the assistant',
  REGENERATION: 'rewritten by the assistant',
  MANUAL: 'edited by hand',
  FEEDBACK_RESOLUTION: 'changed after feedback',
  IMPORT: 'brought in from Ricky Curator',
};

export const GuideHistoryPanel = ({
  itemId,
  currentVersion,
  onRestored,
}: {
  itemId: string;
  currentVersion: number;
  /** Hands the view back to the guide, so a restore ends looking at the text. */
  onRestored: () => void;
}) => {
  const qc = useQueryClient();
  const me = useMe();
  const canWrite = me.data ? canWriteKnowledge(me.data.user) : false;
  const [openId, setOpenId] = useState<string | null>(null);

  const revisions = useQuery({
    queryKey: ['knowledge', 'revisions', itemId],
    queryFn: () => knowledgeApi.revisions(itemId),
    enabled: itemId.length > 0,
  });

  const restore = useMutation({
    mutationFn: (version: number) => knowledgeApi.restoreRevision(itemId, version),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['knowledge'] });
      onRestored();
    },
  });

  if (revisions.isLoading) return <Skeleton className="h-64" />;
  const rows = revisions.data ?? [];
  const current = currentVersion;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {restore.error && (
        <Banner variant="danger">
          <Banner.Description>{(restore.error as Error).message}</Banner.Description>
        </Banner>
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-ink-subtle">
          No earlier versions yet — the history starts at the first save.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-outline bg-surface-elevated">
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Version</Table.HeaderCell>
                <Table.HeaderCell>What happened</Table.HeaderCell>
                <Table.HeaderCell>Who</Table.HeaderCell>
                <Table.HeaderCell>When</Table.HeaderCell>
                <Table.HeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.map((r) => (
                <Table.Row key={r.id}>
                  <Table.Cell className="whitespace-nowrap">
                    v{r.version}
                    {r.version === current && (
                      <Badge variant="success" className="ml-2">
                        current
                      </Badge>
                    )}
                  </Table.Cell>
                  <Table.Cell className="text-ink-muted">
                    {SOURCE_LABEL[r.editSource]}
                    {r.editNote && (
                      <span className="block text-xs text-ink-subtle">{r.editNote}</span>
                    )}
                  </Table.Cell>
                  <Table.Cell className="text-ink-muted">{r.editedByEmail}</Table.Cell>
                  <Table.Cell className="whitespace-nowrap text-ink-muted">
                    {new Date(r.createdAt).toLocaleString()}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setOpenId(openId === r.id ? null : r.id)}
                    >
                      {openId === r.id ? 'Hide' : 'Read'}
                    </Button>
                    {canWrite && r.version !== current && (
                      <Button
                        size="sm"
                        variant="secondary"
                        className="ml-2"
                        onClick={() => restore.mutate(r.version)}
                        disabled={restore.isPending}
                      >
                        {restore.isPending ? 'Restoring…' : 'Restore'}
                      </Button>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </div>
      )}

      {openId !== null && (
        <div className="rounded-lg border border-outline bg-surface-elevated p-6">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-subtle">
            v{rows.find((r) => r.id === openId)?.version} as it was written
          </p>
          <MarkdownViewer>{rows.find((r) => r.id === openId)?.bodyMarkdown ?? ''}</MarkdownViewer>
        </div>
      )}

      {canWrite && rows.length > 0 && (
        <p className="text-xs text-ink-muted">
          Restoring makes that version the current one. It doesn’t go live until someone approves
          it, same as any other edit.
        </p>
      )}
    </div>
  );
};
